import { useCallback, useReducer } from "react";
import type { TableSortByMessageId, TableSortState } from "../ai-chat-message-utils";

export const PANEL_DEFAULT_WIDTH_PX = 480;

interface ChatPanelState {
  inputText: string;
  isAwaitingAgentResponse: boolean;
  panelWidthInPixels: number;
  isClearConversationModalOpen: boolean;
  clearedMessageCount: number;
  view: "chat" | "draft" | "export";
  isDraftResultView: boolean;
  backToSelectionToken: number;
  tableSortByMessageId: TableSortByMessageId;
}

type ChatPanelAction =
  | { type: "setInputText"; inputText: string }
  | { type: "setIsAwaitingAgentResponse"; isAwaitingAgentResponse: boolean }
  | { type: "setPanelWidthInPixels"; panelWidthInPixels: number }
  | { type: "openClearConversationModal" }
  | { type: "closeClearConversationModal" }
  | { type: "confirmClearConversation"; messageCountAtClear: number }
  | { type: "openDraftPanel" }
  | { type: "closeDraftPanel" }
  | { type: "setIsDraftResultView"; isDraftResultView: boolean }
  | { type: "requestBackToSelection" }
  | { type: "openExportPanel" }
  | { type: "closeExportPanel" }
  | { type: "setTableSort"; messageId: string; sort: TableSortState };

const chatPanelInitialState: ChatPanelState = {
  inputText: "",
  isAwaitingAgentResponse: false,
  panelWidthInPixels: PANEL_DEFAULT_WIDTH_PX,
  isClearConversationModalOpen: false,
  clearedMessageCount: 0,
  view: "chat",
  isDraftResultView: false,
  backToSelectionToken: 0,
  tableSortByMessageId: {},
};

const chatPanelReducer = (
  state: ChatPanelState,
  action: ChatPanelAction,
): ChatPanelState => {
  switch (action.type) {
    case "setInputText":
      return { ...state, inputText: action.inputText };
    case "setIsAwaitingAgentResponse":
      return { ...state, isAwaitingAgentResponse: action.isAwaitingAgentResponse };
    case "setPanelWidthInPixels":
      return { ...state, panelWidthInPixels: action.panelWidthInPixels };
    case "openClearConversationModal":
      return { ...state, isClearConversationModalOpen: true };
    case "closeClearConversationModal":
      return { ...state, isClearConversationModalOpen: false };
    case "confirmClearConversation":
      return {
        ...state,
        inputText: "",
        isAwaitingAgentResponse: false,
        isClearConversationModalOpen: false,
        clearedMessageCount: action.messageCountAtClear,
      };
    case "openDraftPanel":
      return { ...state, view: "draft", isDraftResultView: false };
    case "closeDraftPanel":
      return { ...state, view: "chat", isDraftResultView: false };
    case "setIsDraftResultView":
      return { ...state, isDraftResultView: action.isDraftResultView };
    case "requestBackToSelection":
      return { ...state, backToSelectionToken: state.backToSelectionToken + 1 };
    case "openExportPanel":
      return { ...state, view: "export" };
    case "closeExportPanel":
      return { ...state, view: "chat" };
    case "setTableSort":
      return {
        ...state,
        tableSortByMessageId: {
          ...state.tableSortByMessageId,
          [action.messageId]: action.sort,
        },
      };
    default:
      return state;
  }
};

export const useChatPanelState = () => {
  const [state, dispatch] = useReducer(chatPanelReducer, chatPanelInitialState);

  const setInputText = useCallback(
    (inputText: string) => dispatch({ type: "setInputText", inputText }),
    [],
  );
  const setIsAwaitingAgentResponse = useCallback(
    (isAwaitingAgentResponse: boolean) =>
      dispatch({ type: "setIsAwaitingAgentResponse", isAwaitingAgentResponse }),
    [],
  );
  const setPanelWidthInPixels = useCallback(
    (panelWidthInPixels: number) =>
      dispatch({ type: "setPanelWidthInPixels", panelWidthInPixels }),
    [],
  );
  const openClearConversationModal = useCallback(
    () => dispatch({ type: "openClearConversationModal" }),
    [],
  );
  const closeClearConversationModal = useCallback(
    () => dispatch({ type: "closeClearConversationModal" }),
    [],
  );
  const confirmClearConversation = useCallback(
    (messageCountAtClear: number) =>
      dispatch({ type: "confirmClearConversation", messageCountAtClear }),
    [],
  );
  const openDraftPanel = useCallback(() => dispatch({ type: "openDraftPanel" }), []);
  const closeDraftPanel = useCallback(() => dispatch({ type: "closeDraftPanel" }), []);
  const setIsDraftResultView = useCallback(
    (isDraftResultView: boolean) =>
      dispatch({ type: "setIsDraftResultView", isDraftResultView }),
    [],
  );
  const requestBackToSelection = useCallback(
    () => dispatch({ type: "requestBackToSelection" }),
    [],
  );
  const openExportPanel = useCallback(() => dispatch({ type: "openExportPanel" }), []);
  const closeExportPanel = useCallback(() => dispatch({ type: "closeExportPanel" }), []);
  const setTableSort = useCallback(
    (messageId: string, sort: TableSortState) =>
      dispatch({ type: "setTableSort", messageId, sort }),
    [],
  );

  return {
    ...state,
    setInputText,
    setIsAwaitingAgentResponse,
    setPanelWidthInPixels,
    openClearConversationModal,
    closeClearConversationModal,
    confirmClearConversation,
    openDraftPanel,
    closeDraftPanel,
    setIsDraftResultView,
    requestBackToSelection,
    openExportPanel,
    closeExportPanel,
    setTableSort,
  };
};
